import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";

import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";

interface Props {
  /** The full text. Also what the tooltip shows once it no longer fits. */
  text: string;
  className?: string;
  /** Number of lines to keep before clamping. 1 clamps to a single line. */
  lines?: 1 | 2;
  dataTestId?: string;
  /** Element the clamped text renders as, e.g. "h1"/"h2" to keep heading semantics. */
  as?: "span" | "h1" | "h2" | "h3" | "h4";
}

/**
 * Shows `text` clamped to `lines`, and — only when it actually does not fit —
 * reveals the full value in a HintTooltip on hover or tap (same bubble as the
 * device-list status dot: opens above by default, `position:fixed` so table
 * overflow cannot clip it).
 *
 * Overflow is measured rather than guessed, so short values stay plain text
 * with no interaction attached.
 *
 * Purely a visual affordance: the clamping is CSS-only, so the full string is in
 * the DOM either way and assistive technology already reads it. Renders `div`s
 * once clipped (via `HintTooltip`), so keep it out of phrasing-only parents.
 */
export const TruncatedText = ({
  text,
  className,
  lines = 1,
  dataTestId,
  as: Tag = "span",
}: Props) => {
  // A callback ref, not useRef: flipping `isTruncated` re-parents the span into
  // `HintTooltip`, so React unmounts the observed node and mounts a new one. With a
  // plain ref the effect's deps would not change, leaving the ResizeObserver
  // attached to the detached node — after which the value is never re-measured.
  const [textEl, setTextEl] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    if (!textEl) {
      return;
    }
    // +1 guards against sub-pixel rounding reporting a false overflow.
    setIsTruncated(
      lines === 1
        ? textEl.scrollWidth > textEl.clientWidth + 1
        : textEl.scrollHeight > textEl.clientHeight + 1,
    );
  }, [lines, textEl]);

  useEffect(() => {
    if (!textEl) {
      return;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(textEl);

    return () => observer.disconnect();
    // `text` matters as well: a longer value overflows without the box resizing.
  }, [measure, text, textEl]);

  const content = (
    <Tag
      data-testid={dataTestId}
      className={clsx(
        // `line-clamp-2` sets its own required `display: -webkit-box`, which
        // conflicts with `block` (Tailwind resolves same-specificity display
        // utilities by stylesheet order, not by class-attribute order) --
        // only pair `block` with `truncate`, never with `line-clamp-2`.
        lines === 1 ? "block truncate" : "line-clamp-2",
        className,
      )}
      ref={setTextEl}
    >
      {text}
    </Tag>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <HintTooltip
      className="w-full min-w-0 max-w-full cursor-help"
      content={text}
    >
      {content}
    </HintTooltip>
  );
};
